import { getMomentTz, MesfModal } from "@dexteel/mesf-core";
import {
	Button,
	CircularProgress,
	Stack,
	TextField,
	Typography,
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers";
import { Moment } from "moment";
import { useEffect } from "react";
import { Controller, SubmitHandler, useForm } from "react-hook-form";

const moment = getMomentTz();

type Props = {
	open: boolean;
	maxRev: number;
	lastEffectiveDate: Date | null;
	lastUsedDate?: Date | string | null;
	lastUsedLabel?: string;
	allowPastDates?: boolean;
	isSubmitting: boolean;
	onClose: () => void;
	onSubmit: (payload: { rev: number; effectiveDate: Date }) => void;
};

type FormValues = {
	revision: string;
	effectiveDate: Moment | null;
};

export const ConfirmVersionModal = ({
	open,
	maxRev,
	lastEffectiveDate,
	lastUsedDate = null,
	lastUsedLabel = "the last shift inspected with readings",
	allowPastDates = false,
	isSubmitting,
	onClose,
	onSubmit,
}: Props) => {
	const minimumRevision = maxRev + 1;
	const earliest = allowPastDates
		? moment().startOf("day")
		: moment().add(1, "day").startOf("day");
	const lastEffective = lastEffectiveDate
		? moment(lastEffectiveDate).startOf("day")
		: null;
	const lastUsed = lastUsedDate
		? moment(lastUsedDate, "YYYY-MM-DD").startOf("day")
		: null;
	const isUseLater =
		lastUsed !== null &&
		(lastEffective === null || lastUsed.isAfter(lastEffective));
	const lastTaken = isUseLater ? lastUsed : lastEffective;
	const firstAfterLast = lastTaken?.clone().add(1, "day") ?? null;
	const defaultDate =
		firstAfterLast && firstAfterLast.isAfter(earliest)
			? firstAfterLast
			: earliest;
	const minDate = allowPastDates ? (firstAfterLast ?? undefined) : defaultDate;

	const {
		control,
		handleSubmit,
		reset,
		formState: { isValid },
	} = useForm<FormValues>({
		mode: "onChange",
		defaultValues: { revision: "", effectiveDate: null },
	});

	const submit: SubmitHandler<FormValues> = (data) => {
		onSubmit({
			rev: Number(data.revision),
			effectiveDate: (data.effectiveDate as Moment).toDate(),
		});
	};

	useEffect(() => {
		if (open) {
			reset({ revision: String(minimumRevision), effectiveDate: defaultDate });
		}
	}, [open, maxRev, lastEffectiveDate, lastUsedDate]);

	return (
		<MesfModal
			open={open}
			handleClose={onClose}
			maxWidth="sm"
			title="Confirm Draft"
		>
			<form
				data-test-id="confirm-version-modal"
				onSubmit={(e) => {
					e.preventDefault();
					e.stopPropagation();
					handleSubmit(submit)(e);
				}}
			>
				<MesfModal.Content dividers>
					<Stack spacing={2} sx={{ mt: 1 }}>
						<Typography color="textSecondary">
							Confirm this saved draft. Once confirmed, the revision becomes the
							active version as of the effective date below.
						</Typography>
						<Controller
							name="effectiveDate"
							control={control}
							rules={{
								validate: {
									required: (value) => !!value || "Effective date is required",
									future: (value) =>
										allowPastDates ||
										(value && value.isAfter(moment().endOf("day"))) ||
										"Effective date must be greater than today",
									ordered: (value) =>
										!lastTaken ||
										(value && value.isAfter(lastTaken, "day")) ||
										`Effective date must be after ${lastTaken.format("MM/DD/YYYY")}, ${
											isUseLater ? lastUsedLabel : "the last confirmed version"
										}`,
								},
							}}
							render={({ field, fieldState }) => (
								<DatePicker
									label="Effective Date"
									format="MM/DD/YYYY"
									value={field.value}
									minDate={minDate}
									onChange={(newValue) => field.onChange(newValue)}
									slotProps={{
										textField: {
											size: "small",
											// The picker forwards inputProps to its own input;
											// a nested slotProps never reaches it.
											inputProps: {
												"data-test-id": "effective-date-input",
											},
											error: !!fieldState.error,
											helperText:
												fieldState.error?.message ||
												(allowPastDates
													? "A future date leaves the version scheduled"
													: "The version stays scheduled until this date"),
										},
									}}
								/>
							)}
						/>

						<Controller
							name="revision"
							control={control}
							rules={{
								required: "Revision Number is required",
								validate: {
									integer: (value) =>
										(/^\d+$/.test(value) &&
											Number.isSafeInteger(Number(value))) ||
										"Revision Number must be a whole integer",
									minimum: (value) =>
										Number(value) >= minimumRevision ||
										`Revision Number must be at least ${minimumRevision}`,
								},
							}}
							render={({ field, fieldState }) => (
								<TextField
									{...field}
									label="Revision Number"
									required
									size="small"
									slotProps={{
										htmlInput: {
											inputMode: "numeric",
											"data-test-id": "revision-number-input",
										},
									}}
									error={!!fieldState.error}
									helperText={
										fieldState.error?.message ??
										`Minimum Rev: ${minimumRevision}.`
									}
								/>
							)}
						/>
					</Stack>
				</MesfModal.Content>
				<MesfModal.Actions>
					<Button
						onClick={onClose}
						color="inherit"
						disabled={isSubmitting}
						data-test-id="cancel-confirm-version-button"
					>
						Cancel
					</Button>
					<Button
						type="submit"
						variant="contained"
						color="success"
						disabled={isSubmitting || !isValid}
						data-test-id="confirm-version-button"
						startIcon={
							isSubmitting ? (
								<CircularProgress size={16} color="inherit" />
							) : undefined
						}
					>
						{isSubmitting ? "Confirming..." : "Confirm"}
					</Button>
				</MesfModal.Actions>
			</form>
		</MesfModal>
	);
};
